import { useEffect, useState } from 'preact/hooks';
import { lookupCoachHandle } from '../lib/api';
import { PortalFrame } from '../components/PortalFrame';
import './CoachShell.css';

// CoachIntakeLanding — what a stranger sees at `/c/<slug>` (rung 4).
//
// Spec: `spec-the-outside-professional-surface-coaches-and-agents-2026-08-26` §7
// Decision: `828b8687` — a readable handle on a REAL PATH.
//
// ── ⚑ THE ONE SCREEN IN THIS PRODUCT THAT IS NOT ON THE HASH ──
//
// Everything else routes on `#/…`. This does not, and the reason is the whole
// decision: a coach PUBLISHES this URL — a business card, an Instagram bio, a
// studio's website, spoken aloud to a parent — and a `#` survives none of
// those well. The per-tape reviewer links stay hash-routed precisely because
// those ARE capabilities and a fragment is never sent to a server.
// Path for a public handle; hash for a capability.
//
// It works because `/c` is not in staticWeb.ts's RESERVED_PATH_PREFIXES, so
// the path falls through to the SPA catch-all — the same mechanism the
// magic-link and invite landings already use, read from
// `window.location.pathname` in App.tsx.
//
// ── ⚑ IT ASKS FOR NOTHING AND GRANTS NOTHING ──
//
// Spec §7: "THE PARENT GRANTS; THE COACH ONLY EVER ASKS. The link creates
// nothing by itself." So this page has no form, no upload and no button that
// writes: it tells a visitor whose link this is and what happens next. The
// grant is minted later, by the family, from their own tape, on their own
// side. Anything more here would quietly make the coach the one doing the
// granting.

export interface CoachIntakeLandingProps {
  slug: string;
  /** The DEPLOYMENT's product name, not a literal. Hardcoding "CallToWrap"
   * here is the same defect as the "Vowric" it replaced — a name a build
   * asserts rather than reads. `null` until the entry config answers. */
  brand?: string | null;
  /** True when this browser already has a session — the page says something
   * different to a family who is already on the product than to a stranger. */
  signedIn: boolean;
}

type Phase =
  | { kind: 'loading' }
  | {
      kind: 'found';
      displayName: string;
      accepting: boolean;
      /** P4 — the coach's OWN household name, when their tier includes Pro's
       * de-branding (`2f377423`). Null means "wear the product's name", which
       * is what every tier below Pro gets and what a failed lookup gets. */
      brand: string | null;
    }
  | { kind: 'unknown' }
  | { kind: 'error'; message: string };

export function CoachIntakeLanding({ slug, signedIn, brand }: CoachIntakeLandingProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    lookupCoachHandle(slug)
      .then((coach) => {
        if (cancelled) return;
        setPhase(
          coach
            ? {
                kind: 'found',
                displayName: coach.displayName,
                accepting: coach.accepting,
                brand: coach.brand,
              }
            : { kind: 'unknown' }
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // A stranger holding a card must not be told "no such coach" when the
        // truth is that our lookup broke — that sends them to argue with the
        // coach about a link that is fine.
        setPhase({
          kind: 'error',
          message: err instanceof Error ? err.message : 'We could not reach the server.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // ── ⚑ P4 — WHOSE NAME THIS PAGE WEARS (`2f377423`) ──
  //
  // The SERVER decides, exactly as it does for the reviewer portal, and for the
  // same reason: the visitor is a stranger with no session, so the browser
  // cannot be trusted to know — or to be honest about — which tier the coach is
  // on. A non-null answer is the coach's own household name; null means wear
  // the deployment's product name, which is also what an unresolved tier gets.
  //
  // ⚠ NOT KNOWN UNTIL THE LOOKUP LANDS, and `brand` is null until the entry
  // config answers too — PortalFrame holds the space rather than flashing a
  // name and correcting itself. See its own note.
  const houseBrand = phase.kind === 'found' ? phase.brand : null;
  const debranded = houseBrand !== null;

  return (
    <div class="coach-surface">
      <PortalFrame brand={houseBrand ?? brand} footNote="For coaches. Free, always.">
        {phase.kind === 'loading' && <p class="coach__quiet">One moment…</p>}

        {phase.kind === 'error' && (
          <>
            <h1 class="coach__title">We couldn’t check that link</h1>
            <p class="coach__quiet">{phase.message} Please try again in a moment.</p>
          </>
        )}

        {phase.kind === 'unknown' && (
          <>
            <h1 class="coach__title">There’s no coach at this link</h1>
            {/* Says what to do, not merely that it failed. The handle is
                readable, so a typo is the likeliest explanation and the person
                holding the card can fix it themselves. */}
            <p class="coach__quiet">
              Check the spelling with your coach — the link ends in their name.
            </p>
          </>
        )}

        {phase.kind === 'found' && !phase.accepting && (
          <>
            <h1 class="coach__title">{phase.displayName} isn’t taking new tapes here</h1>
            <p class="coach__quiet">
              This link has been turned off. Ask them for a current one — anything you’ve already
              sent is unaffected.
            </p>
          </>
        )}

        {phase.kind === 'found' && phase.accepting && (
          <>
            <h1 class="coach__title">Send {phase.displayName} a tape</h1>
            {signedIn ? (
              <>
                {/* ⚠ THIS SENTENCE POINTS AT A CONTROL AND MUST KEEP MATCHING
                    IT. It described choosing a coach at send time, which no
                    screen has ever offered: the family names the reviewer and
                    hands over the link themselves (AuditionSpread's Tapes tab
                    — the audition's own screen on this pack). An instruction
                    for a door that does not exist is worse than no instruction. */}
                <p class="coach__quiet">
                  You’re signed in. Open the audition you want notes on, add the tape there, then
                  send it for notes — you’ll get a private link to pass to {phase.displayName}.
                </p>
                {/* A link, not a grant. The family does the granting from
                    their own side — see this file's header. The href goes
                    where the label says: the auditions list, which is also
                    where the sentence above just sent them. */}
                <a class="coach__button coach__button--link" href="#/auditions">
                  Go to your auditions
                </a>
              </>
            ) : (
              <>
                {/* ⚑ THE PRODUCT NAMES ITSELF ONLY WHERE IT IS ALLOWED TO.
                    The literal that stood here was the defect this file's own
                    `brand` prop was added to prevent — a name a build asserts
                    rather than reads. On a de-branded (Pro) coach's page the
                    sentence keeps the promise and drops the subject: the office
                    is theirs, and a product pitch in it would be us talking
                    over them. */}
                {debranded || brand === null ? (
                  // ⚠ ALSO THE SENTENCE WHILE THE PRODUCT NAME IS STILL
                  // UNKNOWN. It has no subject to get wrong, so it is correct
                  // in both states — where a placeholder word ("This keeps
                  // your child's auditions…") would be a stand-in a stranger
                  // reads before it corrects itself.
                  <p class="coach__quiet">
                    Keep your auditions in one place and send {phase.displayName} a tape
                    without email attachments. It’s free to start.
                  </p>
                ) : (
                  <p class="coach__quiet">
                    {brand} keeps your auditions in one place and lets you send a tape to{' '}
                    {phase.displayName} without email attachments. It’s free to start.
                  </p>
                )}
                <a class="coach__button coach__button--link" href="#/">
                  Create a free account
                </a>
                <p class="coach__quiet coach__quiet--fine">
                  You choose what to share, one tape at a time. {phase.displayName} sees only what
                  you send.
                </p>
              </>
            )}
          </>
        )}
      </PortalFrame>
    </div>
  );
}
